import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import TableSortLabel from '@mui/material/TableSortLabel';
import PageHeader from './common/PageHeader';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';

export default function Leaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('tokens');
  const [order, setOrder] = useState('desc');
  const [showCampaigns, setShowCampaigns] = useState(false);
  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = a[orderBy] ?? 0;
      const vb = b[orderBy] ?? 0;
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, orderBy, order]);
  
  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
  };
  
  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    api.get('/leaderboard', { params: { limit: 50, includeCampaigns: showCampaigns } })
      .then(r => {
        setRows(r.data);
        setLoading(false);
        setError(null);
      })
      .catch(err => {
        console.error('Error fetching leaderboard:', err);
        setLoading(false);
        setError('Failed to load leaderboard data. Please try again later.');
      });
  }, [user, showCampaigns]);
  
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress />
    </Box>
  );
  
  if (error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
      </Paper>
    );
  }
  
  if (!rows) {
    return null;
  }

  return (
    <> 
      <PageHeader 
        title="Top referrers" 
        subtitle="Most effective inviters by tokens" 
        actions={
          <FormControlLabel 
            control={<Switch checked={showCampaigns} onChange={(e) => setShowCampaigns(e.target.checked)} />} 
            label="Show campaigns" 
          />
        }
      />
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>Direct completed</TableCell>
            <TableCell>2‑level</TableCell>
            <TableCell sortDirection={orderBy === 'tokens' ? order : false} align="right">
              <TableSortLabel active={orderBy === 'tokens'} direction={orderBy === 'tokens' ? order : 'desc'} onClick={() => handleSort('tokens')}>
                Tokens
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.filter(r => (r.directCount || 0) > 0 || (r.grandCount || 0) > 0).map((r, idx) => (
            <TableRow key={r.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Link
                  to={`/user/${r.id}`}
                  state={{ from: { label: 'Referral leaderboard', path: '/leaderboard' } }}
                >
                  {r.username || '(no username)'}
                </Link>
                {r.isCampaign ? <Chip label="Campaign" size="small" sx={{ ml: 1 }} /> : null}
              </TableCell>
              <TableCell align="right">{(r.directCount ?? 0).toLocaleString()}</TableCell>
              <TableCell align="right">{(r.grandCount ?? 0).toLocaleString()}</TableCell>
              <TableCell align="right">{(r.tokens ?? 0).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
    </>
  );
} 